import {
    CmsHeadingElementType,
    CmsHeadingStyle,
} from '@activebrands/core-web/libs/storyblok/content/cms-module-content';
import { RawHeadingData, StoryblokEditable } from '@activebrands/core-web/libs/storyblok/storyblok';
import transformPreviewEditable from '@activebrands/core-web/libs/storyblok/utils/transform-preview-editable';

const HEADING_FALLBACK_STYLE = 'md';
const HEADING_FALLBACK_TYPE = 'h2';

const allowedTypes: { [key: string]: string } = { h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6' };
const allowedStyles: { [key: string]: string } = { sm: 'sm', md: 'md', lg: 'lg' };

export type HeadingProp = RawHeadingData | RawHeadingData[] | undefined;

type TransformHeadingProps = {
    defaultStyle?: CmsHeadingStyle;
    defaultType?: CmsHeadingElementType;
    heading: HeadingProp;
};

export type HeadingType = {
    component?: RawHeadingData['component'];
    id?: string;
    sbEditable?: StoryblokEditable;
    style: CmsHeadingStyle;
    text: string;
    type: CmsHeadingElementType;
};

/**
 * Transforms raw heading data from Storyblok into a standardized heading object
 *
 * @param props - The transformation parameters
 * @param props.defaultStyle - Default style to use if no style is provided
 * @param props.defaultType - Default heading type to use if no type is provided
 * @param props.heading - The raw heading data to transform
 * @param props.heading._editable - The editable of the heading
 * @param props.heading._uid - The uid of the heading
 * @param props.heading.component - The component of the heading
 * @param props.heading.style - The style of the heading
 * @param props.heading.text - The text of the heading
 * @param props.heading.type - The type of the heading
 * @returns Transformed heading object or undefined if no valid heading data
 *
 */
const transformHeading = ({ defaultStyle, defaultType, heading }: TransformHeadingProps): HeadingType | undefined => {
    if (!heading || (Array.isArray(heading) && !heading?.[0])) {
        return;
    }

    const headingData = Array.isArray(heading) ? heading[0] : heading;
    const { _uid, _editable, component, style, text, type } = headingData;

    const customStyle = style && style !== 'default' && allowedStyles[style];
    const fallbackStyle = (defaultStyle && allowedStyles[defaultStyle]) || HEADING_FALLBACK_STYLE;
    const customType = type && type !== 'default' && allowedTypes[type];
    const fallbackType = (defaultType && allowedTypes[defaultType]) || HEADING_FALLBACK_TYPE;

    return {
        component,
        id: _uid,
        sbEditable: transformPreviewEditable(_editable),
        style: (customStyle || fallbackStyle) as CmsHeadingStyle,
        text,
        type: (customType || fallbackType) as CmsHeadingElementType,
    };
};

export default transformHeading;
